/**
 * <SessionRowSkeleton> — la forme de `<SessionRow>` pendant un premier chargement.
 *
 * Posé par les squelettes des deux listes de séances — « À venir » sur l'accueil, « Tous mes
 * lives » : la capsule du tuteur, puis les deux lignes de la séance — la ligne horaire, et la
 * ligne de la séance avec sa chip de matière (`<SessionDetailsLineSkeleton>`). Une seule forme, à
 * toutes les largeurs, comme la ligne.
 *
 * Chez le masteur, rien à la place de la capsule (`lead={false}`), ni la place de son nœud, et sa
 * ligne à lui à la place de celle de la séance (`details`), comme sur sa ligne.
 *
 * Une ligne qui porte un geste à droite (`trailing`, le « GO ! » du masteur) le garde ici : sa forme
 * arrive de l'appelant, posée à la même place que sur la ligne.
 *
 * Les tailles viennent de la ligne elle-même (`AVATAR_SIZE`, `BOW_ROOM`) et de sa 2ᵉ ligne : une
 * retouche de la ligne se voit ici sans recopie.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { CardListRow } from "../CardList"
import { SessionDetailsLineSkeleton } from "../SessionDetailsLine"
import { AVATAR_SIZE, BOW_ROOM, SessionRowTrailingFrame } from "./SessionRow"

export type SessionRowSkeletonProps = {
    /** Comme la ligne : sous un libellé de jour, la séance ne donne que son heure. */
    timeOnly?: boolean
    /** La place de la capsule du tuteur. Défaut `true` ; `false` chez le masteur, dont la ligne n'en a pas. */
    lead?: boolean
    /** La forme de ce que la ligne pose à la place de la ligne de la séance (`details`). Absent : celle-ci. */
    details?: React.ReactNode
    /** La forme du geste que la ligne pose à droite (`trailing`). Absent : rien. */
    trailing?: React.ReactNode
}

export function SessionRowSkeleton(props: SessionRowSkeletonProps) {
    const { timeOnly = false, lead = true, details, trailing } = props

    const row = (
        <CardListRow>
            <Row gap="3" align="center" style={lead ? styles.row : undefined}>
                {lead && <Skeleton shape="circle" size={AVATAR_SIZE} />}
                <Stack gap="1" style={styles.body}>
                    {/* « Vendredi 2 octobre · 13h35 », ou « 13h35 » sous un libellé de jour. */}
                    <Skeleton textSize="text-md" width={timeOnly ? 48 : 190} />
                    {/* « Maths · avec Wajih · 1h ». */}
                    {details !== undefined ? details : <SessionDetailsLineSkeleton width={150} />}
                </Stack>
            </Row>
        </CardListRow>
    )

    if (trailing == null) return row

    return <SessionRowTrailingFrame trailing={trailing}>{row}</SessionRowTrailingFrame>
}

const styles = StyleSheet.create(() => ({
    row: { paddingBottom: BOW_ROOM },
    body: { flex: 1, flexShrink: 1 },
}))
